import React from "react";
import { SIDEBAR_BUTTONS, useSidebarTracking } from "hooks/useSidebarTracking";

const UnitChevron = ({ expanded }) => (
  <svg
    className={`ls-unit-chevron${expanded ? " expanded" : ""}`}
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
  >
    <path d="M9 6L15 12L9 18" stroke="#002E19" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ModuleChevron = ({ expanded }) => (
  <svg
    className={`ls-module-chevron${expanded ? " expanded" : ""}`}
    xmlns="http://www.w3.org/2000/svg"
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
  >
    <path d="M9 6L15 12L9 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const UnitIcon = () => (
  <svg
    className="ls-unit-icon"
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
  >
    <path d="M3 22H21" stroke="#002E19" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M6 18V11" stroke="#002E19" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M10 18V11" stroke="#002E19" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M14 18V11" stroke="#002E19" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M18 18V11" stroke="#002E19" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M12 2L20 7H4L12 2Z" stroke="#002E19" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const LessonSidebarTree = ({
  units,
  selectedLessonId,
  onSelectLesson,
  expandedUnits,
  onToggleUnit,
  expandedModules = {},
  onToggleModule = () => {},
}) => {
  const trackSidebar = useSidebarTracking();
  const toggleUnit = (unit) => {
    trackSidebar(SIDEBAR_BUTTONS.UNIT_TOGGLE, { unit_id: unit.id, action: expandedUnits[unit.id] ? "Collapse" : "Expand" });
    onToggleUnit(unit.id);
  };
  const toggleModule = (module, unit) => {
    trackSidebar(SIDEBAR_BUTTONS.MODULE_TOGGLE, {
      module_id: module.id,
      unit_id: unit.id,
      action: expandedModules[module.id] ? "Collapse" : "Expand",
    });
    onToggleModule(module.id);
  };
  const selectLesson = (lesson, unit) => {
    trackSidebar(SIDEBAR_BUTTONS.SELECT_LESSON, { lesson_id: lesson.id, unit_id: unit.id });
    onSelectLesson(lesson.id);
  };
  const groupLessonsBySection = (lessons) => {
    const groups = [];
    let currentSection = null;
    let currentLessons = [];

    const sortedLessons = [...lessons].sort((a, b) => a.position - b.position);

    for (const lesson of sortedLessons) {
      const section = lesson.section_name || null;
      if (section !== currentSection) {
        if (currentLessons.length > 0) {
          groups.push({ section: currentSection, lessons: currentLessons });
        }
        currentSection = section;
        currentLessons = [lesson];
      } else {
        currentLessons.push(lesson);
      }
    }
    if (currentLessons.length > 0) {
      groups.push({ section: currentSection, lessons: currentLessons });
    }
    return groups;
  };

  const getUnitMeta = (unit) => {
    if (!unit.modules || !unit.modules.length) return null;
    const getOrdinal = (m, idx) => (m.ordinal_name || "").replace(/^Module\s*/i, "") || String(m.position ?? idx + 1);
    const first = getOrdinal(unit.modules[0], 0);
    const last = getOrdinal(unit.modules[unit.modules.length - 1], unit.modules.length - 1);
    return unit.modules.length === 1 ? `Module ${first}` : `Modules ${first} to ${last}`;
  };

  const getModuleMeta = (module) => {
    const lessonCount = module.lessons?.length || 0;
    const lessonsText = lessonCount > 0 ? `${lessonCount} ${lessonCount === 1 ? "lesson" : "lessons"}` : "No lessons yet";
    const standards = (module.standards || [])
      .map((s) => (typeof s === "string" ? s : s.code))
      .filter(Boolean)
      .join(", ");
    return [lessonsText, standards].filter(Boolean).join(" · ");
  };

  const renderLessonList = (lessons, unit) => (
    <div>
      {groupLessonsBySection(lessons).map((group, gi) => (
        <div key={gi}>
          {group.section && (
            <div className="ls-section-header">{group.section}</div>
          )}
          {group.lessons.map((lesson) => (
            <div
              key={lesson.id}
              className={`ls-lesson-item ${lesson.id === selectedLessonId ? "active" : ""}`}
              onClick={() => selectLesson(lesson, unit)}
            >
              <div className="ls-lesson-label">{(lesson.ordinal_name || "").toUpperCase()}</div>
              <div className="ls-lesson-name">{lesson.name}</div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );

  return (
    <div>
      {units.map((unit) => {
        const isExpanded = expandedUnits[unit.id];
        const unitMeta = getUnitMeta(unit);
        return (
          <div key={unit.id}>
            <div className="ls-unit-item" onClick={() => toggleUnit(unit)}>
              <div className="ls-unit-row">
                <UnitChevron expanded={isExpanded} />
                <UnitIcon />
                <div className="ls-unit-text">
                  <div className="ls-unit-label">{(unit.ordinal_name || "").toUpperCase()}</div>
                  <div className="ls-unit-name">{unit.name}</div>
                  {unitMeta && <div className="ls-unit-meta">{unitMeta}</div>}
                </div>
              </div>
            </div>
            {isExpanded && (
              unit.modules ? (
                unit.modules.length === 0 ? (
                  <div className="ls-unit-empty">No modules in this unit yet.</div>
                ) : (
                  <div className="ls-module-list">
                    {unit.modules.map((module) => {
                      const isModuleExpanded = !!expandedModules[module.id];
                      const moduleMeta = getModuleMeta(module);
                      return (
                        <div key={module.id}>
                          <div
                            className="ls-module-row"
                            onClick={() => toggleModule(module, unit)}
                          >
                            <ModuleChevron expanded={isModuleExpanded} />
                            <div className="ls-module-text">
                              <div className="ls-unit-label">{(module.ordinal_name || "").toUpperCase()}</div>
                              <div className="ls-module-name">{module.name}</div>
                              {moduleMeta && <div className="ls-module-meta">{moduleMeta}</div>}
                            </div>
                          </div>
                          {isModuleExpanded && (
                            module.lessons && module.lessons.length > 0 ? (
                              renderLessonList(module.lessons, unit)
                            ) : (
                              <div className="ls-module-empty">
                                Lessons for this module are not available yet.
                              </div>
                            )
                          )}
                        </div>
                      );
                    })}
                  </div>
                )
              ) : (
                unit.lessons && renderLessonList(unit.lessons, unit)
              )
            )}
          </div>
        );
      })}
    </div>
  );
};
